'use client';

import { useAui, useAuiEvent } from '@assistant-ui/react';

import { useConfigValue } from '@/components/providers/config-provider';
import { UNTITLED_THREAD_TITLE } from '@/lib/assistant-cloud-local/runs-api';

/**
 * assistant-ui titles a thread once, after its first run. A first message the server never answered
 * cannot name the thread (it may still be blocked as personal data), so a thread left untitled is
 * titled again after each later run until a checked message names it.
 */
export function useUntitledThreadRetitle(): void {
  const isServerPersistence = useConfigValue<'local' | 'server'>('persistence') === 'server';
  const aui = useAui();

  useAuiEvent('thread.runEnd', ({ threadId }: { threadId: string }) => {
    if (isServerPersistence) {
      return;
    }
    try {
      const item = aui.threads.item({ id: threadId });
      const { status, title } = item.getState();
      if (status === 'new' || title !== UNTITLED_THREAD_TITLE) {
        return;
      }
      void Promise.resolve(item.generateTitle()).catch((error: unknown) =>
        console.warn('[untitled-thread-retitle] retitling failed', { threadId, error }),
      );
    } catch {
      // The thread was deleted meanwhile; there is nothing to title.
    }
  });
}
